import {
  Button,
  Grid,
  Stack,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Typography,
  Modal,
  IconButton,
  Box
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { IconPlus } from '@tabler/icons';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainCard from 'ui-component/cards/MainCard';
import { get, del } from '../../../axios/apiService';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import toast from 'react-hot-toast';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';
import { Visibility } from '@mui/icons-material';

const Template = () => {
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.Template);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);

      try {
        const sortField =
          sortModel.length > 0
            ? sortTemplateField(sortModel[0].field)
            : 'Name';
        const sortDirection =
          sortModel.length > 0
            ? sortModel[0].sort === 'asc'
              ? 'Ascending'
              : 'Descending'
            : 'Ascending';

        const searchQuery = search
          ? `&$filter=Name~LIKE~${encodeURIComponent(search)}`
          : '';

        const url = `EmailTemplate?$skip=${paginationModel.page * paginationModel.pageSize
          }&$top=${paginationModel.pageSize}&$orderby=${sortField}&$orderbydirection=${sortDirection}${searchQuery}`;

        const response = await get(url);

        if (response?.values) {
          const mapped = response.values.map((t) => ({
            id: t.id,
            name: t.name
          }));

          setTemplates(mapped);
          setRowCount(response.totalCount || 0);
        } else {
          setTemplates([]);
          setRowCount(0);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [paginationModel, sortModel, search]);

  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      await del(`EmailTemplate/${deleteId}`);
      toast.success('Template deleted');
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    } catch (err) {
      toast.error('Failed to delete template');
    } finally {
      setDeleteOpen(false);
      setDeleteId('');
      setLoading(false);
    }
  };

  const ActionButtons = (row) => (
    <Stack direction="row" spacing={1}>
      <IconButton
        color="primary"
        onClick={() => navigate(`/marketing/edit-template/${row.id}`)}
      >
        {
          canEdit ? <EditIcon /> : <Visibility />
        }
      </IconButton>
      {
        canEdit && (
          <IconButton
            color="error"
            onClick={() => {
              setDeleteId(row.id);
              setDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          </IconButton>
        )
      }
    </Stack>
  );

  const columns = [
    { field: 'name', headerName: 'Template Name', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      align: 'center',
      renderCell: (params) => <ActionButtons {...params.row} />
    }
  ];

  return (
    <>
      <MainCard title="Template Management">
        <Grid container spacing={3}>
          {/* Search + Add */}
          <Grid item xs={12}>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={1}
            >
              <FormControl sx={{ width: '28ch' }} size="small">
                <OutlinedInput
                  placeholder="Search templates"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  }
                />
              </FormControl>
              {
                canEdit && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<IconPlus />}
                    onClick={() => navigate('/marketing/add-template')}
                  >
                    Add
                  </Button>
                )
              }
            </Stack>
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <DataGrid
              autoHeight
              rows={templates}
              columns={columns}
              rowCount={rowCount}
              getRowId={(row) => row.id}
              paginationMode="server"
              sortingMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={[10]}
              components={{
                NoRowsOverlay: CustomNoRowsOverlay
              }}
            />
          </Grid>
        </Grid>
      </MainCard>

      {/* Delete Modal */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <Box className="modalBox">
          <Typography variant="h6">Delete Template</Typography>
          <Typography sx={{ mt: 2 }}>
            Are you sure you want to delete this template?
          </Typography>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button variant="contained" color="error" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleDelete}>
              Confirm
            </Button>
          </Stack>
        </Box>
      </Modal>

      <LoadingBackdrop open={isLoading} />
    </>
  );
};

export default Template;

const sortTemplateField = (field) => {
  if (field === 'name') return 'Name';
  return 'Name';
};
